import { useCallback, useRef, useState } from 'react'

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

interface UsePendingSavesResult {
  state: SaveState
  error: string | null
  pendingCount: number
  track<T>(blockId: string, run: () => Promise<T>): Promise<T>
  flushAll(): Promise<void>
}

const SAVED_FADE_MS = 2000

export function usePendingSaves(): UsePendingSavesResult {
  const [state, setState] = useState<SaveState>('idle')
  const [error, setError] = useState<string | null>(null)
  const inflight = useRef(new Map<string, Promise<unknown>>())
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const track = useCallback(<T>(blockId: string, run: () => Promise<T>): Promise<T> => {
    setState('saving')
    setError(null)
    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current)
      fadeTimer.current = null
    }
    const task = (async () => {
      try {
        const r = await run()
        return r
      } finally {
        inflight.current.delete(blockId)
      }
    })()
    inflight.current.set(blockId, task)
    return task.then(
      (v) => {
        if (inflight.current.size === 0) {
          setState('saved')
          fadeTimer.current = setTimeout(() => setState('idle'), SAVED_FADE_MS)
        }
        return v
      },
      (err) => {
        setState('error')
        setError(err instanceof Error ? err.message : String(err))
        throw err
      },
    )
  }, [])

  const flushAll = useCallback(async () => {
    const tasks = Array.from(inflight.current.values())
    if (tasks.length === 0) return
    await Promise.allSettled(tasks)
  }, [])

  return { state, error, pendingCount: inflight.current.size, track, flushAll }
}
